import api from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CreateBookingRequest {
  homeId: string;
  assetId: string;
  serviceType: string;
  scheduledDate: string;
  timeSlot: string;
  technicianId: string;
  technicianName: string;
  serviceFee: number;
  notes: string;
  diagnosisData: {
    issue: string;
    rootCauses: string[];
    parts: string[];
    diyAttempted: boolean;
  };
}

export interface Booking {
  id: string;
  bookingId?: string;
  homeId: string;
  userId: string;
  assetId?: string;
  serviceType: string;
  scheduledDate: string;
  timeSlot?: string;
  status: string;
  notes?: string;
  technicianName?: string;
  serviceFee?: number;
  diagnosisIssue?: string;
  diagnosisData?: Record<string, unknown>;
  relatedOrders?: Array<{
    id: string;
    orderId: string;
    productName: string;
    totalAmount: number;
    status: string;
    estimatedDelivery?: string;
    trackingNumber?: string;
  }>;
  createdAt: string;
  updatedAt: string;
  home?: {
    id: string;
    name: string;
    address: string;
  };
  asset?: {
    id: string;
    name: string;
    category: string;
    location?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    imageUrl?: string | null;
  };
}

// ── API Functions ────────────────────────────────────────────────────────────

/**
 * Create a new technician booking
 */
export const createBooking = async (data: CreateBookingRequest): Promise<Booking> => {
  return await api.post<Booking>('/v1/bookings', data);
};

/**
 * Get all bookings for the current user
 */
export const getUserBookings = async (): Promise<Booking[]> => {
  return await api.get<Booking[]>('/v1/bookings');
};

/**
 * Get a specific booking by ID
 */
export const getBookingById = async (bookingId: string): Promise<Booking> => {
  return await api.get<Booking>(`/v1/bookings/${bookingId}`);
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (bookingId: string): Promise<Booking> => {
  return await api.put<Booking>(`/v1/bookings/${bookingId}/cancel`);
};

/**
 * Permanently delete a booking
 */
export const deleteBooking = async (bookingId: string): Promise<{ id: string }> => {
  return await api.delete<{ id: string }>(`/v1/bookings/${bookingId}`);
};

/**
 * Reschedule a booking
 */
export const rescheduleBooking = async (
  bookingId: string,
  newDate: string,
  newTimeSlot: string
): Promise<Booking> => {
  return await api.put<Booking>(`/v1/bookings/${bookingId}/reschedule`, {
    scheduledDate: newDate,
    timeSlot: newTimeSlot,
  });
};

/**
 * Rate a completed booking
 */
export const rateBooking = async (
  bookingId: string,
  rating: number,
  review?: string
): Promise<Booking> => {
  return await api.post<Booking>(`/v1/bookings/${bookingId}/rate`, {
    rating,
    review,
  });
};
